import React, { useEffect, useState } from "react";
import { getOrders, IMAGE_URL } from "../api/api";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import "../styles/MyOrders.css";

const STATUS_CONFIG = {
  pending:    { color: "#F59E0B", bg: "#FFF9E6", icon: "mdi:clock-outline" },
  processing: { color: "#3B82F6", bg: "#EFF6FF", icon: "mdi:progress-clock" },
  confirmed:  { color: "#3B82F6", bg: "#EFF6FF", icon: "mdi:check-circle-outline" },
  delivered:  { color: "#22C55E", bg: "#F0FDF4", icon: "mdi:package-variant-closed-check" },
  cancelled:  { color: "#EF4444", bg: "#FFF5F5", icon: "mdi:close-circle-outline" },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[(status || "").toLowerCase()] || {
    color: "#6B7280", bg: "#F3F4F6", icon: "mdi:help-circle-outline",
  };

const MyOrders = ({ userId }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      setLoading(true);
      getOrders(userId)
        .then((response) => {
          if (Array.isArray(response.data)) {
            const sorted = [...response.data].sort((a, b) => b.id - a.id);
            setOrders(sorted);
          } else {
            setOrders([]);
          }
        })
        .catch(() => setError("Failed to load orders. Please try again."))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [userId]);

  /* ── helpers ── */
  const calcTotal = (items) =>
    items.reduce((sum, i) => sum + Number(i.price) * Number(i.quantity), 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  };

  /* ── Render states ── */
  if (loading) {
    return (
      <div className="mo-page">
        <div className="mo-loading">
          <div className="mo-spinner" />
          <p>Loading your orders…</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="mo-page">
        <div className="mo-empty">
          <div className="mo-empty-icon"><Icon icon="mdi:account-off-outline" width="56" /></div>
          <h2>Sign in to view orders</h2>
          <p>Please log in to see your order history.</p>
          <button className="mo-cta-btn" onClick={() => navigate("/login")}>
            <Icon icon="mdi:login" width="20" /> Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mo-page">
      {/* Header */}
      <div className="mo-header">
        <div className="mo-header-inner">
          <div>
            <h1 className="mo-title">My Orders</h1>
            <p className="mo-subtitle">
              {orders.length > 0
                ? `${orders.length} order${orders.length > 1 ? "s" : ""} placed`
                : "No orders yet"}
            </p>
          </div>
          <button className="mo-order-now-btn" onClick={() => navigate("/menu")}>
            <Icon icon="mdi:plus" width="18" /> Order More
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mo-content">
        {error && (
          <div className="mo-error-banner">
            <Icon icon="mdi:alert-circle-outline" width="20" />
            {error}
          </div>
        )}

        {orders.length === 0 ? (
          <div className="mo-empty">
            <div className="mo-empty-icon"><Icon icon="mdi:receipt-text-remove-outline" width="64" /></div>
            <h2>No orders yet</h2>
            <p>Looks like you haven't placed any orders. Start exploring our menu!</p>
            <button className="mo-cta-btn" onClick={() => navigate("/menu")}>
              <Icon icon="mdi:food-outline" width="20" /> Browse Menu
            </button>
          </div>
        ) : (
          <div className="mo-orders-list">
            {orders.map((order) => {
              let items = [];
              try { 
                items = JSON.parse(order.order_details); 
              } catch {
                try {
                  // Attempt to parse older double-escaped records
                  items = JSON.parse(order.order_details.replace(/\\"/g, '"'));
                } catch {}
              }
              const statusCfg = getStatusConfig(order.status);
              const calculatedTotal = calcTotal(items);
              const total = calculatedTotal > 0 ? calculatedTotal : Number(order.total_amount || 0);

              return (
                <div key={order.id} className="mo-card">
                  {/* Card header */}
                  <div className="mo-card-header">
                    <div className="mo-order-meta">
                      <span className="mo-order-id">Order #{order.id}</span>
                      <span className="mo-order-date">
                        <Icon icon="mdi:calendar-outline" width="14" />
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                    <span
                      className="mo-status-badge"
                      style={{ color: statusCfg.color, background: statusCfg.bg }}
                    >
                      <Icon icon={statusCfg.icon} width="15" />
                      {order.status
                        ? order.status.charAt(0).toUpperCase() + order.status.slice(1)
                        : "Unknown"}
                    </span>
                  </div>

                  {/* Items list */}
                  <div className="mo-items">
                    {items.map((item, idx) => (
                      <div key={idx} className="mo-item">
                        <div className="mo-item-img-wrap">
                          <img
                            src={`${IMAGE_URL}${item.image}`}
                            alt={item.name}
                            className="mo-item-img"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/64x64?text=🍔"; }}
                          />
                        </div>
                        <div className="mo-item-info">
                          <span className="mo-item-name">{item.name}</span>
                          <span className="mo-item-qty">Qty: {item.quantity}</span>
                        </div>
                        <span className="mo-item-price">
                          ₹{(Number(item.price) * Number(item.quantity)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Card footer */}
                  <div className="mo-card-footer">
                    <span className="mo-items-count">
                      <Icon icon="mdi:food-outline" width="16" />
                      {items.length} item{items.length !== 1 ? "s" : ""}
                    </span>
                    <div className="mo-total">
                      <span className="mo-total-label">Total</span>
                      <span className="mo-total-value">₹{total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
